import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Table } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import { fetchAllPPCDataByIDAPI, filterCallByParams } from "../../utils/api";
import withAuth from "../../utils/withAuth";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { numberFormat } from "../../components/numberFormat";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import ReportPPCComponents from "../../components/report/ReportPPCComponents";

const RADIAN = Math.PI / 180;

// Draws line + % label outside the donut
const renderDonutLabelWithLine = (props) => {
    const {
        cx,
        cy,
        midAngle,
        outerRadius,
        value,        // our % value (share_percent)
        fill,
        payload,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const labelText = `${payload.name} (${Number(value).toFixed(1)}%)`;

    // start: at outer edge of slice
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;

    // middle point: a bit further out from slice
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 10) * sin;

    // end point: horizontal offset (left or right)
    const ex = mx + (cos >= 0 ? 22 : -22);
    const ey = my;

    const textAnchor = cos >= 0 ? "start" : "end";

    return (
        <g>
            <polyline
                points={`${sx},${sy} ${mx},${my} ${ex},${ey}`}
                stroke={fill || "#999"}
                strokeWidth={1}
                fill="none"
            />
            <circle cx={ex} cy={ey} r={2} fill={fill || "#999"} />
            <text
                x={ex + (cos >= 0 ? 4 : -4)}
                y={ey}
                textAnchor={textAnchor}
                dominantBaseline="central"
                style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    fill: "#333",
                    letterSpacing: "0.5px",
                }}
            >
                {labelText}
            </text>
        </g>
    );
};

// Colors for donut slices
const DONUT_COLORS = [
    "#4285F4",
    "#FBBC05",
    "#34A853",
    "#EA4335",
    "#A142F4",
    "#00ACC1",
    "#f41ed7ff",
    "#7CB342",
    "#35fdb0ff",
    "#8D6E63",
];

const PPCPerformance = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState([]);
    const [searchSkillRaw, setSearchSkillRaw] = useState("");
    const [searchSkill, setSearchSkill] = useState("");
    const [isCustomRange, setIsCustomRange] = useState(false);

    const defaultStart = useMemo(() => moment().startOf("month"), []);
    const defaultEnd = useMemo(() => moment().endOf("day"), []);
    const [dateRange, setDateRange] = useState({
        startDate: defaultStart,
        endDate: defaultEnd,
    });

    useEffect(() => {
        const t = setTimeout(() => setSearchSkill(searchSkillRaw.trim()), 250);
        return () => clearTimeout(t);
    }, [searchSkillRaw]);

    const drpRef = useRef(null);

    useEffect(() => {
        loadCurrentMonth();
    }, []);

    const loadCurrentMonth = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllPPCDataByIDAPI(
                "get-ppc-team-last-seven-days-performance-report"
            );
            if (res?.data?.status === 1) {
                setProcess(res.data.process || []);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
            }
        } catch (err) {
            console.error(err);
            toast.error("Network/CORS error while loading process");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterByDate = async (_event, picker) => {
        setIsLoading(true);
        const start = picker.startDate.clone().startOf("day");
        const end = picker.endDate.clone().endOf("day");

        setDateRange({ startDate: start, endDate: end });
        setIsCustomRange(true);

        try {
            const res = await filterCallByParams(
                "get-ppc-team-filter-performance-report",
                {
                    startDate: start.format("YYYY-MM-DD"),
                    endDate: end.format("YYYY-MM-DD"),
                }
            );
            if (res?.data?.status === 1) setProcess(res.data.process || []);
            if (drpRef.current) {
                drpRef.current.innerText = `${start.format(
                    "DD MMM YYYY"
                )} – ${end.format("DD MMM YYYY")}`;
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching filtered report");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = async () => {
        if (drpRef.current) drpRef.current.innerText = "Select Date Range";
        setIsCustomRange(false);
        setDateRange({ startDate: defaultStart, endDate: defaultEnd });
        await loadCurrentMonth();
    };

    const filteredProcess = useMemo(() => {
        const q = searchSkill.toLowerCase();
        if (!q) return process;
        return (process || []).filter((item) => {
            const fields = [item?.ppc_name];
            return fields.some((f) =>
                String(f || "").toLowerCase().includes(q)
            );
        });
    }, [process, searchSkill]);

    // Totals based on the columns you render below (including cost + factor)
    const totals = useMemo(() => {
        const n = (v) => Number(v) || 0;

        return filteredProcess.reduce(
            (acc, item) => {
                acc.orders_count += n(item.orders_count);
                acc.value += n(item.value);
                acc.delivered_value += n(item.delivered_value);
                acc.delivered_count += n(item.delivered_count);
                acc.calls_unique_skill_server += n(
                    item.calls_unique_skill_server
                );
                acc.calls_unique_exclusive += n(
                    item.calls_unique_exclusive
                );

                acc.total_cost += n(item.total_cost);
                acc.sum_call_cost += n(item.call_cost);
                acc.sum_factor += n(item.factor);

                // sums for averages
                acc.sum_conv_pct += n(item.conv_pct);
                acc.sum_del_pct += n(item.del_pct);
                acc.sum_tkt_size += n(item.tkt_size);
                acc.rows += 1;

                return acc;
            },
            {
                orders_count: 0,
                value: 0,
                delivered_value: 0,
                delivered_count: 0,
                calls_unique_skill_server: 0,
                calls_unique_exclusive: 0,

                total_cost: 0,
                sum_call_cost: 0,
                sum_factor: 0,

                // for averages
                sum_conv_pct: 0,
                sum_del_pct: 0,
                sum_tkt_size: 0,
                rows: 0,
            }
        );
    }, [filteredProcess]);

    const avgConv = totals.rows ? totals.sum_conv_pct / totals.rows : 0;
    const avgDel = totals.rows ? totals.sum_del_pct / totals.rows : 0;
    const avgTkt = totals.rows ? totals.sum_tkt_size / totals.rows : 0;

    // aggregate call_cost & factor like backend logic for the "Grand Total" row
    const grandCallCost =
        totals.calls_unique_skill_server > 0 && totals.total_cost > 0
            ? (totals.total_cost / totals.calls_unique_skill_server).toFixed(2)
            : "0.00";

    const grandFactor =
        totals.total_cost > 0
            ? (totals.value / totals.total_cost).toFixed(2)
            : "0.00";

    // Donut data: PPC-wise Conversion %
    const donutData = useMemo(() => {
        return (filteredProcess || [])
            .map((item) => {
                const raw = item.conv_pct ?? 0;
                const num = parseFloat(String(raw).toString().replace("%", ""));
                return {
                    name: item.ppc_name || "NA",
                    value: Number.isNaN(num) ? 0 : num,
                };
            })
            .filter((d) => d.value > 0)
            .sort((a, b) => b.value - a.value)
            .slice(0, 25);
    }, [filteredProcess]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::PPC REPORT</title>
            </Helmet>
            <ContentWrapper>
                <ReportNavBar />
                <ReportPPCComponents />
                <div className="row mt-2">
                    <div className="col-lg-8 mb-3">
                        <div
                            className="card hrpages"
                            style={{ marginBottom: "55px" }}
                        >
                            <div className="heading p-2 d-flex flex-wrap gap-2 justify-content-between items-center">
                                <h5
                                    className="mb-0 text-uppercase"
                                    style={{ fontWeight: "600" }}
                                >
                                    PPC Performance (
                                    {isCustomRange
                                        ? `${dateRange.startDate.format(
                                            "DD MMM YYYY"
                                        )} - ${dateRange.endDate.format(
                                            "DD MMM YYYY"
                                        )}`
                                        : "LAST 7 DAY`S"}
                                    )
                                </h5>

                                <div style={{ width: "25%", minWidth: 260 }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search By PPC Name"
                                        value={searchSkillRaw}
                                        onChange={(e) =>
                                            setSearchSkillRaw(e.target.value)
                                        }
                                    />
                                </div>

                                <div className="d-flex gap-2">
                                    <DateRangePicker
                                        initialSettings={{
                                            startDate: dateRange.startDate,
                                            endDate: dateRange.endDate,
                                            autoUpdateInput: false,
                                            showDropdowns: true,
                                            alwaysShowCalendars: true,
                                            locale: { format: "DD MMM YYYY" },
                                        }}
                                        onApply={handleFilterByDate}
                                    >
                                        <button
                                            className="btn btn-sm btndaterange"
                                            ref={drpRef}
                                            type="button"
                                        >
                                            Select Date Range
                                        </button>
                                    </DateRangePicker>
                                    <span
                                        onClick={handleClear}
                                        className="btnreset"
                                        style={{ marginBottom: "5px" }}
                                    >
                                        Reset
                                    </span>
                                </div>
                            </div>

                            <div className="table pagetable table-responsive">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead>
                                        <tr>
                                            {/* span only # and PPC Name */}
                                            <th
                                                colSpan={2}
                                                className="text-end"
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                Grand Total
                                            </th>

                                            {/* Totals */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.calls_unique_skill_server}
                                            </th>
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.calls_unique_exclusive}
                                            </th>

                                            {/* Count of Orders */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.orders_count}
                                            </th>

                                            {/* Volume */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(totals.value)}
                                            </th>

                                            {/* Total Cost */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(totals.total_cost)}
                                            </th>

                                            {/* Delivered value */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(
                                                    totals.delivered_value
                                                )}
                                            </th>

                                            {/* Delivered count */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.delivered_count}
                                            </th>

                                            {/* Avg Conv % */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {avgConv.toFixed(2)}%
                                            </th>

                                            {/* Call/Cost (grand) */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {grandCallCost}
                                            </th>

                                            {/* Avg Del % */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {avgDel.toFixed(2)}%
                                            </th>

                                            {/* Avg Ticket Size */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(avgTkt)}
                                            </th>

                                            {/* Grand Factor */}
                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 12,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {grandFactor}
                                            </th>
                                        </tr>
                                    </thead>

                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>PPC Name</th>
                                            <th>Call(Skill)</th>
                                            <th>Calls(All Skill)</th>
                                            <th>Orders</th>
                                            <th>Volume</th>
                                            <th>Total Cost</th>
                                            <th>Delivered</th>
                                            <th>Del Count</th>
                                            <th>Conv(%)</th>
                                            <th>Call/Cost</th>
                                            <th>Del(%)</th>
                                            <th>Ticket Size</th>
                                            <th>Factor</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoading ? (
                                            [...Array(12)].map((_, i) => (
                                                <tr key={i}>
                                                    {Array.from({
                                                        length: 14,
                                                    }).map((_, index) => (
                                                        <td key={index}>
                                                            <Skeleton animation="wave" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : filteredProcess.length > 0 ? (
                                            filteredProcess.map(
                                                (item, index) => (
                                                    <tr
                                                        key={`${item.ppc_name}-${index}`}
                                                    >
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            {item.ppc_name
                                                                ? item.ppc_name
                                                                    .length > 7
                                                                    ? item.ppc_name
                                                                        .substring(
                                                                            0,
                                                                            7
                                                                        )
                                                                        .toUpperCase() +
                                                                    "..."
                                                                    : item.ppc_name.toUpperCase()
                                                                : "UNKNOWN"}
                                                        </td>
                                                        <td>
                                                            {
                                                                item.calls_unique_skill_server
                                                            }
                                                        </td>
                                                        <td>
                                                            {
                                                                item.calls_unique_exclusive
                                                            }
                                                        </td>
                                                        <td>
                                                            {Number(
                                                                item.orders_count
                                                            ) || 0}
                                                        </td>
                                                        <td>
                                                            {numberFormat(
                                                                item.value
                                                            )}
                                                        </td>
                                                        <td>
                                                            {numberFormat(
                                                                item.total_cost
                                                            )}
                                                        </td>
                                                        <td>
                                                            {numberFormat(
                                                                item.delivered_value
                                                            )}
                                                        </td>
                                                        <td>
                                                            {Number(
                                                                item.delivered_count
                                                            ) || 0}
                                                        </td>
                                                        <td>
                                                            {item.conv_pct}
                                                        </td>
                                                        <td>
                                                            {numberFormat(
                                                                item.call_cost
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.del_pct}
                                                        </td>
                                                        <td>
                                                            {numberFormat(
                                                                item.tkt_size
                                                            )}
                                                        </td>
                                                        <td>
                                                            {Number(
                                                                item.factor
                                                            ).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={14}
                                                    className="text-center"
                                                >
                                                    No matching skills found...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                    <div
                        className="col-lg-4 mb-3"
                        style={{ background: "#fff", borderRadius:"5px" }}
                    >
                        <div className="d-flex justify-content-center align-items-center py-3">
                            {donutData.length > 0 && (
                                <PieChart width={600} height={300}>
                                    <Pie
                                        data={donutData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={95}
                                        paddingAngle={3}
                                        label={renderDonutLabelWithLine}
                                        labelLine={false}
                                    >
                                        {donutData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    DONUT_COLORS[
                                                    index %
                                                    DONUT_COLORS.length
                                                    ]
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    <text
                                        x="50%"
                                        y="50%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize={12}
                                        fill="#333"
                                    >
                                        PPC Conv%
                                    </text>

                                    <Tooltip
                                        formatter={(v, _n, payload) => [
                                            `${Number(v).toFixed(2)}%`,
                                            payload.payload.name,
                                        ]}
                                    />
                                </PieChart>
                            )}
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(PPCPerformance);
